import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Devices as DevicesIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DeviceType, DeviceSize, PaymentStatus, InventoryItem, DeviceTypeModel, DeviceSizeModel, DeviceAccessoryModel, DeviceBrandModel, Branch, Warehouse, Technician } from '../utils/types';
import { addCustomer, searchCustomers, checkPhoneExists } from '../services/customerService';
import { useCurrency } from '../hooks/useCurrency';
import { useSettings } from '../contexts/SettingsContext';

import { addDevice } from '../services/maintenanceService';
import { createEnhancedMaintenanceRecord, createMultiDeviceMaintenanceRecord, createSeparateMaintenanceRecords, updateMaintenanceRecord } from '../services/maintenanceService';
import { Device, MaintenanceDevice } from '../utils/types';
import { Customer } from '../types/customer';
import { useAuth } from '../contexts/AuthContext';
import WhatsAppMessageSender from '../components/WhatsAppMessageSender';
import { getAllInventoryItems } from '../services/inventoryService';
import { getAllDeviceTypes, getAllDeviceSizes, getAllDeviceAccessories, getAllDeviceBrands, getDefaultDeviceType } from '../services/settingsService';
import { getTechniciansByBranch } from '../services/technicianService';
import { getAllBranches } from '../services/branchService';
import { getAllWarehouses } from '../services/inventoryService';
import { MaintenanceStatusCategoryService, MaintenanceStatusSubCategoryService } from '../services/maintenanceStatusService';
import { MaintenanceStatusCategory, MaintenanceStatusSubCategory } from '../types/maintenanceStatus';
// دالة بسيطة للتحقق من رقم الهاتف
interface PhoneValidationResult {
  isValid: boolean;
  message: string;
}

const validatePhoneNumber = (phone: string, phoneDigitsCount: number, phoneValidationEnabled: boolean): Promise<PhoneValidationResult> => {
  return new Promise((resolve) => {
    // إذا كان التحقق من رقم الهاتف معطل، اعتبر الرقم صحيح
    if (!phoneValidationEnabled) {
      resolve({
        isValid: true,
        message: 'رقم الهاتف صحيح'
      });
      return;
    }

    // إزالة المسافات والرموز
    const cleanPhone = phone.replace(/\D/g, '');

    // التحقق من طول الرقم حسب الإعدادات
    if (cleanPhone.length !== phoneDigitsCount) {
      resolve({
        isValid: false,
        message: `رقم الهاتف يجب أن يكون مكون من ${phoneDigitsCount} رقم`
      });
      return;
    }

    resolve({
      isValid: true,
      message: 'رقم الهاتف صحيح'
    });
  });
};



const MaintenanceCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { formatCurrency } = useCurrency();
  const { settings } = useSettings();

  // حالة البحث عن عميل
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // حالة معلومات العميل الجديد
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    branch: '',
    notes: '',
    alternatePhone: ''
  });

  // حالة التحقق من رقم الهاتف
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResult | null>(null);
  const [alternatePhoneValidation, setAlternatePhoneValidation] = useState<PhoneValidationResult | null>(null);

  // حالة إضافة جهاز جديد
  const [newDeviceData, setNewDeviceData] = useState<Partial<Device>>({
    type: undefined,
    typeId: '',
    brand: '',
    brandId: '',
    model: '',
    serialNumber: '',
    size: undefined,
    sizeId: ''
  });

  // إدارة عدة أجهزة
  const [isMultiDeviceMode, setIsMultiDeviceMode] = useState(false);
  const [devices, setDevices] = useState<Omit<MaintenanceDevice, 'id' | 'status' | 'completedAt' | 'deliveredAt'>[]>([]);
  const [separateOrders, setSeparateOrders] = useState(true); // true = طلبات منفصلة، false = طلب واحد متعدد

  // ملحقات الصيانة
  const [accessories, setAccessories] = useState<string[]>([]);
  const [customAccessory, setCustomAccessory] = useState('');

  // حالة معلومات الصيانة
  const [maintenanceData, setMaintenanceData] = useState({
    issue: '',
    initialCost: 0,
    finalCost: 0,
    maintenanceFee: 0,
    paid: 0,
    remainingAmount: 0,
    estimatedDeliveryDate: '',
    receivingEmployee: '',
    paymentStatus: 'unpaid' as PaymentStatus,
    notes: '',
    requireCallBeforeMaintenance: false,
    costAfterContact: 0
  });



  // Estado para controlar si estamos editando un recibo existente
  const [isEditingReceipt, setIsEditingReceipt] = useState(false);

  // المنتجات المستخدمة في الصيانة
  const [usedParts, setUsedParts] = useState<{
    itemId: string;
    name: string;
    quantity: number;
    price: number;
    total: number;
    warehouseId: string;
  }[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemPrice, setItemPrice] = useState(0);
  const [loadingInventory, setLoadingInventory] = useState(false);



  // قوائم الإعدادات
  const [deviceTypesList, setDeviceTypesList] = useState<DeviceTypeModel[]>([]);
  const [deviceSizesList, setDeviceSizesList] = useState<DeviceSizeModel[]>([]);
  const [deviceAccessoriesList, setDeviceAccessoriesList] = useState<DeviceAccessoryModel[]>([]);
  const [deviceBrandsList, setDeviceBrandsList] = useState<DeviceBrandModel[]>([]);
  const [loadingSettings, setLoadingSettings] = useState({
    types: false,
    sizes: false,
    accessories: false,
    brands: false
  });

  // الفروع والمخازن
  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  // الفنيين
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<Technician | null>(null);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);

  // حالات الصيانة
  const [maintenanceStatusCategories, setMaintenanceStatusCategories] = useState<MaintenanceStatusCategory[]>([]);
  const [maintenanceStatusSubCategories, setMaintenanceStatusSubCategories] = useState<MaintenanceStatusSubCategory[]>([]);
  const [selectedMaintenanceStatusCategory, setSelectedMaintenanceStatusCategory] = useState<string>('');
  const [selectedMaintenanceStatusSubCategory, setSelectedMaintenanceStatusSubCategory] = useState<string>('');
  const [customMaintenanceStatus, setCustomMaintenanceStatus] = useState<string>('');
  const [useCustomStatus, setUseCustomStatus] = useState(false);
  const [loadingMaintenanceStatus, setLoadingMaintenanceStatus] = useState(false);

  // حالات أخرى
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingNewCustomer, setIsAddingNewCustomer] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'info' | 'warning'
  });

  // حساب القيم المالية عند تغيير البيانات
  useEffect(() => {
    calculateFinancialValues();
  }, [usedParts, maintenanceData.initialCost, maintenanceData.paid, maintenanceData.finalCost, isEditingReceipt]);

  // البحث عن العملاء
  const searchForCustomers = async () => {
    if (searchTerm.length < 3) return;

    setIsSearching(true);
    try {
      const results = await searchCustomers(searchTerm);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء البحث عن العملاء',
        severity: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  };

  // البحث عن العملاء عند تغيير مصطلح البحث
  useEffect(() => {
    if (searchTerm.length >= 3) {
      searchForCustomers();
    }
  }, [searchTerm]);

  // تحميل جميع البيانات عند تحميل الصفحة
  useEffect(() => {
    loadInventoryItems();
    loadDeviceTypes();
    loadDeviceSizes();
    loadDeviceAccessories();
    loadDeviceBrands();
    loadBranchesAndWarehouses();
    loadMaintenanceStatusData();
  }, []);

  // تحميل البيانات من قاعدة البيانات أولاً، ثم من التخزين المحلي إذا لزم الأمر
  useEffect(() => {
    // لا نحتاج إلى تحميل البيانات من التخزين المحلي هنا
    // لأن الدوال المعدلة في settingsService ستقوم بذلك إذا لزم الأمر
    // نحم نقوم بتحميل البيانات من قاعدة البيانات فقط
  }, []);

  // تحميل أنواع الأجهزة
  const loadDeviceTypes = async () => {
    setLoadingSettings({ ...loadingSettings, types: true });
    try {
      const types = await getAllDeviceTypes(true);
      setDeviceTypesList(types);

      // تعيين الجهاز الافتراضي إذا لم يكن هناك جهاز محدد
      if (types.length > 0 && !newDeviceData.typeId) {
        try {
          // البحث عن الجهاز الافتراضي
          const defaultType = await getDefaultDeviceType();
          const selectedType = defaultType || types[0];

          // Check if the name is a valid DeviceType
          const typeName = selectedType.name as DeviceType;
          setNewDeviceData(prev => ({
            ...prev,
            typeId: selectedType.id,
            type: typeName
          }));

        } catch (e) {
          // Use a valid default value
          setNewDeviceData(prev => ({
            ...prev,
            typeId: types[0].id,
            type: 'أخرى'
          }));
        }
      }
    } catch (error) {
      console.error('Error loading device types:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل أنواع الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoadingSettings({ ...loadingSettings, types: false });
    }
  };

  // تحميل أحجام الأجهزة
  const loadDeviceSizes = async () => {
    setLoadingSettings({ ...loadingSettings, sizes: true });
    try {
      const sizes = await getAllDeviceSizes(true);
      setDeviceSizesList(sizes);

      // تعيين القيمة الافتراضية إذا كانت هناك عناصر في القائمة
      if (sizes.length > 0 && !newDeviceData.sizeId) {
        try {
          // Check if the name is a valid DeviceSize
          const sizeName = sizes[0].name as DeviceSize;
          setNewDeviceData(prev => ({
            ...prev,
            sizeId: sizes[0].id,
            size: sizeName
          }));

        } catch (e) {
          // Use a valid default value
          setNewDeviceData(prev => ({
            ...prev,
            sizeId: sizes[0].id,
            size: 'medium'
          }));
        }
      }
    } catch (error) {
      console.error('Error loading device sizes:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل أحجام الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoadingSettings({ ...loadingSettings, sizes: false });
    }
  };

  // تحميل ملحقات الأجهزة
  const loadDeviceAccessories = async () => {
    setLoadingSettings({ ...loadingSettings, accessories: true });
    try {
      const accessories = await getAllDeviceAccessories(true);
      setDeviceAccessoriesList(accessories);
    } catch (error) {
      console.error('Error loading device accessories:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل ملحقات الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoadingSettings({ ...loadingSettings, accessories: false });
    }
  };

  // تحميل ماركات الأجهزة
  const loadDeviceBrands = async () => {
    setLoadingSettings({ ...loadingSettings, brands: true });
    try {
      const brands = await getAllDeviceBrands(true);
      setDeviceBrandsList(brands);

      // تعيين القيمة الافتراضية إذا كانت هناك عناصر في القائمة
      if (brands.length > 0 && !newDeviceData.brandId) {
        setNewDeviceData(prev => ({
          ...prev,
          brandId: brands[0].id,
          brand: brands[0].name
        }));

      }
    } catch (error) {
      console.error('Error loading device brands:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل ماركات الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoadingSettings({ ...loadingSettings, brands: false });
    }
  };

  // تحميل الفروع والمخازن
  const loadBranchesAndWarehouses = async () => {
    try {
      // تحميل الفروع
      const branchList = await getAllBranches();
      setBranches(branchList);

      // تعيين الفرع الافتراضي
      if (branchList.length > 0) {
        setSelectedBranch(branchList[0]);
        // تحميل فنيي الفرع الافتراضي
        loadTechnicians(branchList[0].id);
      }

      // تحميل المخازن
      const warehouseList = await getAllWarehouses();
      setWarehouses(warehouseList);

      // تعيين المخزن الافتراضي (يفضل أن يكون مرتبطًا بالفرع المختار)
      if (warehouseList.length > 0) {
        const defaultWarehouse = branchList.length > 0
          ? warehouseList.find(w => w.branchId === branchList[0].id) || warehouseList[0]
          : warehouseList[0];

        setSelectedWarehouse(defaultWarehouse);
      }
    } catch (error) {
      console.error('Error loading branches and warehouses:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات الفروع والمخازن',
        severity: 'error'
      });
    }
  };

  // تحميل الفنيين حسب الفرع
  const loadTechnicians = async (branchId: string) => {
    try {
      setLoadingTechnicians(true);
      const techniciansList = await getTechniciansByBranch(branchId, true);
      setTechnicians(techniciansList);

      // إعادة تعيين الفني المختار إذا لم يعد متاحاً في الفرع الجديد
      if (selectedTechnician && !techniciansList.find(t => t.id === selectedTechnician.id)) {
        setSelectedTechnician(null);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات الفنيين',
        severity: 'error'
      });
    } finally {
      setLoadingTechnicians(false);
    }
  };

  // تحميل حالات الصيانة
  const loadMaintenanceStatusData = async () => {
    try {
      setLoadingMaintenanceStatus(true);
      const [categories, subCategories] = await Promise.all([
        MaintenanceStatusCategoryService.getCategories(),
        MaintenanceStatusSubCategoryService.getSubCategories()
      ]);
      setMaintenanceStatusCategories(categories);
      setMaintenanceStatusSubCategories(subCategories);
    } catch (error) {
      console.error('Error loading maintenance status data:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل حالات الصيانة',
        severity: 'error'
      });
    } finally {
      setLoadingMaintenanceStatus(false);
    }
  };

  // حساب القيم المالية
  const calculateFinancialValues = () => {
    // حساب تكلفة قطع الغيار (للعرض فقط وليس للحساب)
    // ملاحظة: قطع الغيار لا تؤثر على التكلفة النهائية

    // القيمة النهائية = القيمة المبدئية فقط (بدون تكلفة قطع الغيار)
    let finalCost = maintenanceData.initialCost;

    // إذا كنا نقوم بتعديل إيصال موجود وتم تحديد قيمة نهائية مختلفة
    if (isEditingReceipt && maintenanceData.finalCost > 0) {
      finalCost = maintenanceData.finalCost;
    }

    // أجر الصيانة = القيمة المبدئية (تكلفة العمل اليدوي)
    const maintenanceFee = Math.max(0, maintenanceData.initialCost);

    // حساب المبلغ المتبقي
    const remainingAmount = Math.max(0, finalCost - maintenanceData.paid);

    // ملاحظة: تكلفة قطع الغيار لا تؤثر على التكلفة النهائية
    // سيتم استخدام تكلفة قطع الغيار فقط في تقرير المكسب

    setMaintenanceData({
      ...maintenanceData,
      finalCost,
      maintenanceFee,
      remainingAmount
    });
  };

  // تحميل المنتجات من المخزون
  const loadInventoryItems = async () => {
    setLoadingInventory(true);
    try {
      const items = await getAllInventoryItems();
      setInventoryItems(items);
    } catch (error) {
      console.error('Error loading inventory items:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل المنتجات',
        severity: 'error'
      });
    } finally {
      setLoadingInventory(false);
    }
  };

  // التحقق من رقم الهاتف
  const handlePhoneValidation = async (phone: string, isAlternate: boolean = false) => {
    if (!phone || phone.trim() === '') {
      if (isAlternate) {
        setAlternatePhoneValidation(null);
      } else {
        setPhoneValidation(null);
      }
      return;
    }

    try {
      // التحقق من صحة تنسيق رقم الهاتف
      const validation = await validatePhoneNumber(phone, settings.phoneDigitsCount, settings.phoneValidationEnabled);

      if (validation.isValid) {
        // التحقق من وجود رقم الهاتف مسبقاً
        const existingCustomer = await checkPhoneExists(phone);
        if (existingCustomer) {
          if (isAlternate) {
            setAlternatePhoneValidation({
              isValid: false,
              message: `رقم الهاتف مسجل مسبقاً باسم: ${existingCustomer.name}`
            });
          } else {
            setPhoneValidation({
              isValid: false,
              message: `رقم الهاتف مسجل مسبقاً باسم: ${existingCustomer.name}`
            });
          }
        } else {
          if (isAlternate) {
            setAlternatePhoneValidation(validation);
          } else {
            setPhoneValidation(validation);
          }
        }
      } else {
        if (isAlternate) {
          setAlternatePhoneValidation(validation);
        } else {
          setPhoneValidation(validation);
        }
      }
    } catch (error) {
      console.error('Error validating phone number:', error);
    }
  };

  // إضافة جهاز إلى القائمة
  const handleAddDevice = async () => {
    // التحقق من البيانات المطلوبة
    if (!selectedCustomer) {
      setSnackbar({
        open: true,
        message: 'يرجى اختيار عميل أولاً',
        severity: 'error'
      });
      return;
    }

    if (!newDeviceData.type) {
      setSnackbar({
        open: true,
        message: 'يرجى اختيار نوع الجهاز',
        severity: 'error'
      });
      return;
    }

    // التحقق من إدخال حالة الصيانة
    const maintenanceStatusText = getMaintenanceStatusText();
    
    if (!maintenanceStatusText) {
      setSnackbar({
        open: true,
        message: 'يرجى اختيار فئة فرعية أو كتابة وصف مخصص للمشكلة',
        severity: 'error'
      });
      return;
    }

    // التحقق من صحة البيانات الإضافية
    if (newDeviceData.serialNumber && newDeviceData.serialNumber.trim() === '') {
      setNewDeviceData({ ...newDeviceData, serialNumber: undefined });
    }

    if (newDeviceData.model && newDeviceData.model.trim() === '') {
      setNewDeviceData({ ...newDeviceData, model: undefined });
    }

    try {
      // إنشاء جهاز جديد إذا لم يكن موجوداً
      let deviceId = selectedDeviceId;
      if (!deviceId && selectedCustomer) {
        // إنشاء الجهاز مباشرة
        console.log('إنشاء جهاز جديد...');

        const deviceData = {
          customerId: selectedCustomer.id,
          type: newDeviceData.type as DeviceType,
          typeId: newDeviceData.typeId || '',
          brand: newDeviceData.brand || '',
          brandId: newDeviceData.brandId || '',
          model: newDeviceData.model || '',
          serialNumber: newDeviceData.serialNumber || '',
          size: newDeviceData.size as DeviceSize,
          sizeId: newDeviceData.sizeId || '',
          createdAt: new Date(),
          updatedAt: new Date()
        };

        console.log('بيانات الجهاز المراد إضافته:', deviceData);
        const newDevice = await addDevice(deviceData);
        deviceId = newDevice.id;
        console.log('تم إنشاء الجهاز بنجاح، المعرف:', deviceId);
      }

      // إنشاء كائن الجهاز للصيانة مع تنظيف البيانات
      const maintenanceDevice: any = {
        deviceId: deviceId || '',
        deviceType: newDeviceData.type as DeviceType,
        brand: newDeviceData.brand || '',
        model: newDeviceData.model || '',
        serialNumber: newDeviceData.serialNumber || '',
        issue: maintenanceStatusText,
        diagnosis: '',
        accessories: [...accessories],
        usedParts: [], // سنبدأ بقائمة فارغة ويمكن إضافة القطع لاحقاً
        notes: ''
      };

      // إضافة الحجم فقط إذا كان موجوداً
      if (newDeviceData.size) {
        maintenanceDevice.size = newDeviceData.size;
      }

      // إضافة الجهاز إلى القائمة
      setDevices([...devices, maintenanceDevice]);

      // إعادة تعيين النموذج
      setNewDeviceData({
        type: undefined,
        typeId: '',
        brand: '',
        brandId: '',
        model: '',
        serialNumber: '',
        size: undefined,
        sizeId: ''
      });
      setCustomMaintenanceStatus('');
      setSelectedMaintenanceStatusCategory('');
      setSelectedMaintenanceStatusSubCategory('');
      setUseCustomStatus(false);
      setAccessories([]);
      setUsedParts([]);
      setSelectedDeviceId('');

      setSnackbar({
        open: true,
        message: 'تم إضافة الجهاز بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding device:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إضافة الجهاز',
        severity: 'error'
      });
    }
  };

  // حذف جهاز من القائمة
  const handleRemoveDevice = (index: number) => {
    const updatedDevices = devices.filter((_, i) => i !== index);
    setDevices(updatedDevices);
  };

  // العودة إلى صفحة الصيانة
  const handleGoBack = () => {
    navigate('/admin/maintenance');
  };

  // اختيار عميل من نتائج البحث
  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsAddingNewCustomer(false);
  };

  // إضافة عميل جديد
  const handleAddNewCustomer = async () => {
    // التحقق من إدخال البيانات المطلوبة
    if (!newCustomer.name || !newCustomer.phone) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال اسم العميل ورقم الهاتف',
        severity: 'error'
      });
      return;
    }

    // التحقق من صحة الاسم
    if (!/^[\u0600-\u06FFa-zA-Z\s]+$/.test(newCustomer.name.trim())) {
      setSnackbar({
        open: true,
        message: 'اسم العميل يجب أن يحتوي على حروف فقط',
        severity: 'error'
      });
      return;
    }

    // التحقق من طول الاسم
    if (newCustomer.name.trim().length < 2) {
      setSnackbar({
        open: true,
        message: 'اسم العميل يجب أن يكون حرفين على الأقل',
        severity: 'error'
      });
      return;
    }

    // التحقق من صحة رقم الهاتف
    const phoneValidationResult = await validatePhoneNumber(newCustomer.phone, settings.phoneDigitsCount, settings.phoneValidationEnabled);
    if (!phoneValidationResult.isValid) {
      setSnackbar({
        open: true,
        message: phoneValidationResult.message,
        severity: 'error'
      });
      return;
    }

    // التحقق من رقم الهاتف البديل إذا تم إدخاله
    if (newCustomer.alternatePhone) {
      const alternatePhoneValidationResult = await validatePhoneNumber(newCustomer.alternatePhone, settings.phoneDigitsCount, settings.phoneValidationEnabled);
      if (!alternatePhoneValidationResult.isValid) {
        setSnackbar({
          open: true,
          message: `رقم الهاتف البديل: ${alternatePhoneValidationResult.message}`,
          severity: 'error'
        });
        return;
      }
    }

    try {
      const addedCustomer = await addCustomer(
        newCustomer.name.trim(),
        newCustomer.phone,
        newCustomer.email || '',
        newCustomer.address || '',
        newCustomer.branch || '',
        newCustomer.notes || '',
        newCustomer.alternatePhone
      );

      if (addedCustomer) {
        setSelectedCustomer(addedCustomer);
      }
      setNewCustomer({
        name: '',
        phone: '',
        email: '',
        address: '',
        branch: '',
        notes: '',
        alternatePhone: ''
      });
      setIsAddingNewCustomer(false);

      setSnackbar({
        open: true,
        message: 'تم إضافة العميل بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding customer:', error);

      // التحقق من نوع الخطأ
      let errorMessage = 'حدث خطأ أثناء إضافة العميل';

      if (error instanceof Error) {
        if (error.message.includes('مسجل مسبقاً')) {
          errorMessage = error.message;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // إضافة منتج إلى قائمة المنتجات المستخدمة
  const handleAddPart = () => {
    if (!selectedItem || itemQuantity <= 0) return;

    const item = inventoryItems.find(i => i.id === selectedItem);
    if (!item) return;

    // استخدام السعر المدخل أو سعر المنتج الافتراضي
    const unitPrice = itemPrice > 0 ? itemPrice : item.sellingPrice || 0;
    const total = unitPrice * itemQuantity;

    // التحقق من عدم تكرار إضافة نفس المنتج
    const existingPartIndex = usedParts.findIndex(p => p.itemId === selectedItem);
    if (existingPartIndex >= 0) {
      // تحديث الكمية والسعر الإجمالي للمنتج الموجود
      const newParts = [...usedParts];
      newParts[existingPartIndex].quantity += itemQuantity;
      newParts[existingPartIndex].price = unitPrice;
      newParts[existingPartIndex].total = newParts[existingPartIndex].quantity * unitPrice;
      setUsedParts(newParts);
    } else {
      // إضافة منتج جديد
      setUsedParts([
        ...usedParts,
        {
          itemId: item.id,
          name: item.name,
          quantity: itemQuantity,
          price: unitPrice,
          total: total,
          warehouseId: selectedWarehouse?.id || 'default'
        }
      ]);
    }

    // إعادة تعيين قيم الإدخال
    setSelectedItem('');
    setItemQuantity(1);
    setItemPrice(0);
  };

  // إزالة منتج من قائمة المنتجات المستخدمة
  const handleRemovePart = (index: number) => {
    const newParts = [...usedParts];
    newParts.splice(index, 1);
    setUsedParts(newParts);
  };

  // تحديث الملحقات
  const handleAccessoryChange = (accessory: string, checked: boolean) => {
    if (checked) {
      setAccessories([...accessories, accessory]);
    } else {
      setAccessories(accessories.filter(a => a !== accessory));
    }
  };

  // إضافة ملحق مخصص
  const handleAddCustomAccessory = () => {
    if (customAccessory && !accessories.includes(customAccessory)) {
      setAccessories([...accessories, customAccessory]);
      setCustomAccessory('');
    }
  };

  // اختيار نوع الجهاز
  const handleDeviceTypeChange = (typeId: string) => {
    const selectedType = deviceTypesList.find(type => type.id === typeId);
    if (selectedType) {
      try {
        // Check if it's a valid DeviceType
        const typeName = selectedType.name as DeviceType;
        setNewDeviceData({
          ...newDeviceData,
          typeId,
          type: typeName
        });
      } catch (e) {
        console.error('Invalid device type:', e);
        // Use a valid default
        setNewDeviceData({
          ...newDeviceData,
          typeId,
          type: 'أخرى'
        });
      }
    }
  };

  // اختيار ماركة الجهاز
  const handleDeviceBrandChange = (brandId: string) => {
    const selectedBrand = deviceBrandsList.find(brand => brand.id === brandId);
    if (selectedBrand) {
      setNewDeviceData({
        ...newDeviceData,
        brandId,
        brand: selectedBrand.name
      });
    }
  };

  // اختيار حجم الجهاز
  const handleDeviceSizeChange = (sizeId: string) => {
    const selectedSize = deviceSizesList.find(size => size.id === sizeId);
    if (selectedSize) {
      try {
        // Check if it's a valid DeviceSize
        const sizeName = selectedSize.name as DeviceSize;
        setNewDeviceData({
          ...newDeviceData,
          sizeId,
          size: sizeName
        });
      } catch (e) {
        console.error('Invalid device size:', e);
        // Use a valid default
        setNewDeviceData({
          ...newDeviceData,
          sizeId,
          size: 'medium'
        });
      }
    }
  };

  // تحديث الفئات الفرعية عند اختيار فئة رئيسية
  const handleMaintenanceStatusCategoryChange = (categoryId: string) => {
    setSelectedMaintenanceStatusCategory(categoryId);
    setSelectedMaintenanceStatusSubCategory(''); // إعادة تعيين الفئة الفرعية
  };

  // تحديث النص المخصص عند اختيار فئة فرعية
  const handleMaintenanceStatusSubCategoryChange = (subCategoryId: string) => {
    setSelectedMaintenanceStatusSubCategory(subCategoryId);
    const selectedSubCategory = maintenanceStatusSubCategories.find(sub => sub.id === subCategoryId);
    if (selectedSubCategory) {
      setCustomMaintenanceStatus(selectedSubCategory.name);
    }
  };

  // تحديث النص المخصص عند الكتابة اليدوية
  const handleCustomMaintenanceStatusChange = (text: string) => {
    setCustomMaintenanceStatus(text);
    setUseCustomStatus(true);
    setSelectedMaintenanceStatusSubCategory(''); // إلغاء اختيار الفئة الفرعية
  };

  // دالة مساعدة لتحديد حالة الصيانة
  const getMaintenanceStatusText = () => {
    const hasSelectedStatus = selectedMaintenanceStatusSubCategory && selectedMaintenanceStatusSubCategory.trim() !== '';
    const hasCustomStatus = customMaintenanceStatus && customMaintenanceStatus.trim() !== '';
    
    if (hasSelectedStatus) {
      return maintenanceStatusSubCategories.find(sub => sub.id === selectedMaintenanceStatusSubCategory)?.name || customMaintenanceStatus;
    } else if (hasCustomStatus) {
      return customMaintenanceStatus;
    } else {
      return '';
    }
  };

  // حفظ طلب الصيانة
  const handleSubmit = async () => {
    // التحقق من إدخال البيانات المطلوبة
    if (!selectedCustomer) {
      setSnackbar({
        open: true,
        message: 'يرجى اختيار عميل أولاً',
        severity: 'error'
      });
      return;
    }

    // التحقق من وجود أجهزة للصيانة أو حالة صيانة
    if (devices.length === 0) {
      const maintenanceStatusText = getMaintenanceStatusText();
      
      if (!maintenanceStatusText) {
        setSnackbar({
          open: true,
          message: 'يرجى اختيار فئة فرعية أو كتابة وصف مخصص للمشكلة أو إضافة أجهزة للصيانة',
          severity: 'error'
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // بدء عملية إنشاء طلب الصيانة
      console.log('بدء إنشاء طلب الصيانة...');

      // التحقق من وجود الفرع والمخزن
      if (!selectedBranch || !selectedWarehouse) {
        setSnackbar({
          open: true,
          message: 'يرجى اختيار فرع ومخزن صالحين',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }

      // إذا كان هناك أجهزة متعددة، استخدم الدالة المناسبة
      if (devices.length > 0) {
        try {
          if (separateOrders) {
            // إنشاء طلبات منفصلة لكل جهاز
            console.log('Creating separate maintenance records for each device...');

            const createdRecords = await createSeparateMaintenanceRecords(
              selectedCustomer.id,
              selectedCustomer.name,
              devices,
              selectedBranch.id,
              selectedWarehouse.id,
              maintenanceData.estimatedDeliveryDate ? new Date(maintenanceData.estimatedDeliveryDate) : undefined,
              currentUser?.id,
              currentUser?.name || 'مستخدم النظام',
              maintenanceData.notes
            );

            console.log('Separate maintenance records created:', createdRecords);

            setSnackbar({
              open: true,
              message: `تم إنشاء ${createdRecords.length} طلبات صيانة منفصلة بنجاح! أرقام الطلبات: ${createdRecords.map(r => r.orderNumber).join(', ')}`,
              severity: 'success'
            });

            // الانتقال إلى صفحة الصيانة الرئيسية
            setTimeout(() => {
              navigate('/admin/maintenance');
            }, 2000);
            return;

          } else {
            // إنشاء طلب صيانة واحد متعدد الأجهزة
            console.log('Creating single multi-device maintenance record...');

            const maintenanceRecord = await createMultiDeviceMaintenanceRecord(
              selectedCustomer.id,
              selectedCustomer.name,
              devices,
              selectedBranch.id,
              selectedWarehouse.id,
              maintenanceData.estimatedDeliveryDate ? new Date(maintenanceData.estimatedDeliveryDate) : undefined,
              currentUser?.id,
              currentUser?.name || 'مستخدم النظام',
              maintenanceData.notes
            );

            console.log('Multi-device maintenance created:', maintenanceRecord);

            setSnackbar({
              open: true,
              message: `تم إنشاء طلب صيانة متعدد الأجهزة بنجاح! رقم الطلب: ${maintenanceRecord.orderNumber}`,
              severity: 'success'
            });

            // الانتقال إلى صفحة تفاصيل الصيانة
            setTimeout(() => {
              navigate(`/admin/maintenance/order-${maintenanceRecord.orderNumber}`);
            }, 1500);
            return;
          }

        } catch (error) {
          console.error('Error creating maintenance records:', error);
          console.error('Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
            selectedCustomer: selectedCustomer?.id,
            devicesCount: devices.length,
            selectedBranch: selectedBranch?.id,
            selectedWarehouse: selectedWarehouse?.id,
            devices: devices,
            separateOrders
          });

          const errorMessage = error instanceof Error ? error.message : 'حدث خطأ غير معروف';
          const orderType = separateOrders ? 'طلبات الصيانة المنفصلة' : 'طلب الصيانة متعدد الأجهزة';
          setSnackbar({
            open: true,
            message: `حدث خطأ أثناء إنشاء ${orderType}: ${errorMessage}`,
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }
      }

      // الكود الأصلي للجهاز الواحد
      let deviceId = selectedDeviceId;

      // إذا لم يتم اختيار جهاز، أضف جهازًا جديدًا
      if (!deviceId) {
        if (!newDeviceData.typeId || !newDeviceData.brandId) {
          setSnackbar({
            open: true,
            message: 'يرجى إدخال نوع الجهاز والماركة',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }

        try {
          // إضافة جهاز جديد
          const deviceData = {
            customerId: selectedCustomer.id,
            type: newDeviceData.type as DeviceType,
            typeId: newDeviceData.typeId,
            brand: newDeviceData.brand,
            brandId: newDeviceData.brandId,
            model: newDeviceData.model,
            serialNumber: newDeviceData.serialNumber,
            size: newDeviceData.size as DeviceSize,
            sizeId: newDeviceData.sizeId,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          console.log('جاري إضافة جهاز جديد:', deviceData);
          const newDevice = await addDevice(deviceData);
          deviceId = newDevice.id;
          console.log('تم إضافة الجهاز بنجاح، المعرف:', deviceId);
        } catch (deviceError) {
          console.error('خطأ في إضافة الجهاز:', deviceError);

          // تحديد رسالة الخطأ المناسبة بناءً على نوع الخطأ
          let errorMessage = 'حدث خطأ أثناء إضافة الجهاز. يرجى المحاولة مرة أخرى.';

          if (deviceError instanceof Error) {
            console.error('تفاصيل الخطأ:', {
              message: deviceError.message,
              stack: deviceError.stack,
              deviceData: {
                customerId: selectedCustomer?.id,
                type: newDeviceData.type,
                brand: newDeviceData.brand,
                model: newDeviceData.model,
                serialNumber: newDeviceData.serialNumber
              }
            });

            // تحليل نوع الخطأ وتقديم رسالة مناسبة
            if (deviceError.message.includes('permission-denied') || deviceError.message.includes('صلاحية')) {
              errorMessage = `❌ مشكلة في صلاحيات قاعدة البيانات

🔧 الحل:
1. اذهب إلى Firebase Console
2. Firestore Database > Rules
3. تحديث قواعد الأمان
4. أو تواصل مع المطور

💡 يمكنك استخدام أداة التشخيص لمزيد من التفاصيل`;
            } else if (deviceError.message.includes('network')) {
              errorMessage = 'مشكلة في الاتصال بالإنترنت. يرجى التحقق من الاتصال والمحاولة مرة أخرى.';
            } else if (deviceError.message.includes('Firestore')) {
              errorMessage = 'مشكلة في قاعدة البيانات. يرجى المحاولة مرة أخرى لاحقاً.';
            } else if (deviceError.message.includes('required')) {
              errorMessage = 'بعض البيانات المطلوبة مفقودة. يرجى التحقق من جميع الحقول.';
            }
          }

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }
      }

      // إنشاء طلب صيانة جديد
      const estimatedDate = maintenanceData.estimatedDeliveryDate
        ? new Date(maintenanceData.estimatedDeliveryDate)
        : undefined;

      console.log('جاري إنشاء طلب صيانة جديد للعميل:', selectedCustomer.name, 'وللجهاز:', deviceId);

      try {
        // التحقق من وجود المخزن قبل إنشاء طلب الصيانة
        if (!selectedWarehouse) {
          setSnackbar({
            open: true,
            message: 'يرجى اختيار مخزن صالح',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }

        // التحقق من وجود الفرع قبل إنشاء طلب الصيانة
        if (!selectedBranch) {
          setSnackbar({
            open: true,
            message: 'يرجى اختيار فرع صالح',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }

        // التحقق من صحة قطع الغيار المستخدمة
        if (usedParts && usedParts.length > 0) {
          for (const part of usedParts) {
            if (!part.itemId) {
              setSnackbar({
                open: true,
                message: `قطعة الغيار "${part.name}" غير صالحة`,
                severity: 'error'
              });
              setIsSubmitting(false);
              return;
            }
          }
        }

        // إنشاء طلب الصيانة باستخدام الدالة المحسنة
        const maintenanceRecord = await createEnhancedMaintenanceRecord(
          deviceId,
          selectedCustomer.id,
          selectedCustomer.name,
          newDeviceData.type as DeviceType,
          newDeviceData.brand || '',
          newDeviceData.model || '',
          newDeviceData.serialNumber || '',
          newDeviceData.size,
          getMaintenanceStatusText(),
          maintenanceData.initialCost,
          selectedBranch.id,
          selectedWarehouse.id,
          estimatedDate,
          currentUser?.id,
          maintenanceData.receivingEmployee || currentUser?.name || 'مستخدم النظام',
          accessories,
          usedParts,
          maintenanceData.finalCost,
          maintenanceData.paid,
          maintenanceData.paymentStatus,
          maintenanceData.notes,
          maintenanceData.requireCallBeforeMaintenance,
          maintenanceData.costAfterContact
        );

        console.log('تم إنشاء طلب الصيانة بنجاح:', maintenanceRecord);

        // تحديث السجل بمعرف الفني إذا تم اختياره
        if (selectedTechnician && maintenanceRecord.id) {
          try {
            await updateMaintenanceRecord(maintenanceRecord.id, {
              technicianId: selectedTechnician.id,
              technicianName: selectedTechnician.name
            });
          } catch (error) {
            console.warn('فشل في ربط الفني بطلب الصيانة:', error);
          }
        }

        setSnackbar({
          open: true,
          message: 'تم إنشاء طلب الصيانة بنجاح',
          severity: 'success'
        });

        // الانتقال إلى صفحة تفاصيل الصيانة
        setTimeout(() => {
          navigate(`/admin/maintenance/order-${maintenanceRecord.orderNumber}`);
        }, 1500);
      } catch (maintenanceError) {
        console.error('خطأ في إنشاء طلب الصيانة:', maintenanceError);

        // تحديد رسالة الخطأ المناسبة
        let errorMessage = 'حدث خطأ أثناء إنشاء طلب الصيانة. تم حفظ البيانات محليًا، يرجى المحاولة مرة أخرى لاحقًا.';

        // إذا كان الخطأ يتعلق بالمخزن
        if (maintenanceError instanceof Error) {
          if (maintenanceError.message.includes('المخزن غير موجود') ||
              maintenanceError.message.includes('المخزن غير مرتبط')) {
            errorMessage = `خطأ في المخزن: ${maintenanceError.message}. يرجى التحقق من اختيار مخزن صالح.`;
          } else if (maintenanceError.message.includes('الفرع غير موجود')) {
            errorMessage = `خطأ في الفرع: ${maintenanceError.message}. يرجى التحقق من اختيار فرع صالح.`;
          } else if (maintenanceError.message.includes('الكمية غير كافية') ||
                     maintenanceError.message.includes('الكمية المتوفرة')) {
            errorMessage = `خطأ في المخزون: ${maintenanceError.message}. يرجى التحقق من توفر الكميات المطلوبة.`;
          }
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });

        // حفظ البيانات للمحاولة لاحقًا
        try {
          const backupData = {
            timestamp: new Date().toISOString(),
            deviceId,
            customerId: selectedCustomer.id,
            customerName: selectedCustomer.name,
            deviceType: newDeviceData.type,
                    issue: getMaintenanceStatusText(),
            initialCost: maintenanceData.initialCost,
            estimatedDeliveryDate: estimatedDate,
            accessories,
            usedParts,
            finalCost: maintenanceData.finalCost,
            paid: maintenanceData.paid,
            paymentStatus: maintenanceData.paymentStatus,
            notes: maintenanceData.notes,
            error: maintenanceError instanceof Error ? maintenanceError.message : 'خطأ غير معروف'
          };

          localStorage.setItem('maintenance_form_backup_' + new Date().getTime(), JSON.stringify(backupData));
          console.log('تم حفظ بيانات الطلب محليًا للاستخدام لاحقًا');
        } catch (saveError) {
          console.error('خطأ في حفظ البيانات محليًا:', saveError);
        }
      }
    } catch (error) {
      console.error('خطأ عام في إنشاء طلب الصيانة:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      p: 3
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="contained"
          onClick={handleGoBack}
          sx={{
            mb: 3,
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            }
          }}
        >
          العودة إلى قائمة الصيانة
        </Button>

        {/* Modern Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 2,
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
              }}
            >
              <DevicesIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              إنشاء طلب صيانة جديد
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              نظام إدارة الصيانة المتطور
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: 4,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
      >



        {/* قسم اختيار الفرع والمخزن */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
          }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}
            >
              <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>
                ⚙️
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              إعدادات الفرع والمخزن
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)'
                    }
                  }
                }}
              >
                <InputLabel>🏢 الفرع</InputLabel>
                <Select
                  value={selectedBranch?.id || ''}
                  label="🏢 الفرع"
                  onChange={(e) => {
                    const branch = branches.find(b => b.id === e.target.value);
                    setSelectedBranch(branch || null);

                    // تحديث المخزن تلقائيًا عند تغيير الفرع
                    if (branch) {
                      const branchWarehouse = warehouses.find(w => w.branchId === branch.id);
                      if (branchWarehouse) {
                        setSelectedWarehouse(branchWarehouse);
                      }

                      // تحميل فنيي الفرع
                      loadTechnicians(branch.id);
                    }
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            mr: 1
                          }}
                        />
                        {branch.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)'
                    }
                  }
                }}
              >
                <InputLabel>📦 المخزن</InputLabel>
                <Select
                  value={selectedWarehouse?.id || ''}
                  label="📦 المخزن"
                  onChange={(e) => {
                    const warehouse = warehouses.find(w => w.id === e.target.value);
                    setSelectedWarehouse(warehouse || null);
                  }}
                >
                  {warehouses
                    .filter(w => !selectedBranch || w.branchId === selectedBranch.id)
                    .map((warehouse) => (
                      <MenuItem key={warehouse.id} value={warehouse.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'secondary.main',
                              mr: 1
                            }}
                          />
                          {warehouse.name}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* اختيار الفني */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>👨‍🔧 الفني المسؤول</InputLabel>
                <Select
                  value={selectedTechnician?.id || ''}
                  label="👨‍🔧 الفني المسؤول"
                  disabled={loadingTechnicians || !selectedBranch}
                  onChange={(e) => {
                    const technician = technicians.find(t => t.id === e.target.value);
                    setSelectedTechnician(technician || null);
                  }}
                >
                  <MenuItem value="">
                    <em>لا يوجد فني محدد</em>
                  </MenuItem>
                  {technicians.map((technician) => (
                    <MenuItem key={technician.id} value={technician.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: technician.isActive ? 'success.main' : 'grey.400',
                            mr: 1
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {technician.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {technician.specialization}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* قسم معلومات العميل */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            p: 3,
            borderRadius: 3,
            background: selectedCustomer
              ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            border: `2px solid ${selectedCustomer ? 'rgba(76, 175, 80, 0.3)' : 'rgba(102, 126, 234, 0.3)'}`,
            transition: 'all 0.3s ease'
          }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: selectedCustomer
                  ? 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3,
                boxShadow: selectedCustomer
                  ? '0 8px 24px rgba(76, 175, 80, 0.3)'
                  : '0 8px 24px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              <Typography sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.5rem'
              }}>
                {selectedCustomer ? '✓' : '1'}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: selectedCustomer ? 'success.main' : 'primary.main',
                  mb: 0.5
                }}
              >
                {selectedCustomer ? '✅ تم اختيار العميل' : '👤 الخطوة الأولى'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                {selectedCustomer
                  ? `العميل المختار: ${selectedCustomer.name}`
                  : 'البحث عن عميل موجود أو تسجيل عميل جديد'
                }
              </Typography>
            </Box>
          </Box>
        </Box>

        {!selectedCustomer && !isAddingNewCustomer ? (
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                border: '2px solid rgba(102, 126, 234, 0.2)',
                mb: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.15)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <SearchIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  البحث عن عميل موجود
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="ابحث بالاسم أو رقم الهاتف"
                    placeholder="مثال: أحمد محمد أو 01234567890"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isSearching ? (
                            <CircularProgress size={24} />
                          ) : (
                            <SearchIcon sx={{ color: 'primary.main' }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem',
                        borderRadius: 2,
                        bgcolor: 'white',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}>
                      أو
                    </Typography>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => setIsAddingNewCustomer(true)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                      boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #45a049 0%, #7cb342 100%)',
                        boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    ➕ تسجيل عميل جديد
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {searchResults.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}
              >
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{
                        '& .MuiTableCell-head': {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem'
                        }
                      }}>
                        <TableCell>👤 الاسم</TableCell>
                        <TableCell>📱 رقم الهاتف</TableCell>
                        <TableCell>📞 رقم الهاتف البديل</TableCell>
                        <TableCell>📱 عدد الأجهزة</TableCell>
                        <TableCell align="center">⚡ الإجراءات</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((customer) => (
                        <TableRow
                          key={customer.id}
                          sx={{
                            '&:nth-of-type(odd)': {
                              bgcolor: 'rgba(102, 126, 234, 0.02)'
                            },
                            '&:hover': {
                              bgcolor: 'rgba(102, 126, 234, 0.08)',
                              transform: 'scale(1.01)',
                              transition: 'all 0.2s ease'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 2,
                                  color: 'white',
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                {customer.name.charAt(0)}
                              </Box>
                              {customer.name}
                            </Box>
                          </TableCell>
                          <TableCell>{customer.phone}</TableCell>
                          <TableCell>{customer.alternatePhone || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={customer.devices?.length || 0}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleSelectCustomer(customer)}
                              sx={{
                                borderRadius: 2,
                                px: 3,
                                background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #45a049 0%, #7cb342 100%)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                }
                              }}
                            >
                              ✅ اختيار
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {searchTerm.length >= 3 && searchResults.length === 0 && !isSearching && (
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                لم يتم العثور على نتائج للبحث. جرب بحثًا آخر أو أضف عميلًا جديدًا.
              </Typography>
            )}
          </Box>
        ) : null}

        {isAddingNewCustomer && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{
              p: 3,
              bgcolor: 'success.light',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'success.main',
              mb: 3
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'success.dark', fontWeight: 'bold' }}>
                ➕ تسجيل عميل جديد
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="اسم العميل"
                    placeholder="مثال: أحمد محمد علي"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    placeholder={`${'0'.repeat(settings.phoneDigitsCount)}`}
                    value={newCustomer.phone}
                    onChange={(e) => {
                      setNewCustomer({ ...newCustomer, phone: e.target.value });
                      handlePhoneValidation(e.target.value, false);
                    }}
                    required
                    error={phoneValidation ? !phoneValidation.isValid : false}
                    helperText={phoneValidation ? phoneValidation.message : (settings.phoneValidationEnabled ? `يجب أن يكون ${settings.phoneDigitsCount} رقم` : '')}
                    color={phoneValidation && phoneValidation.isValid ? 'success' : 'primary'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف البديل (اختياري)"
                    placeholder={`${'0'.repeat(settings.phoneDigitsCount)}`}
                    value={newCustomer.alternatePhone}
                    onChange={(e) => {
                      setNewCustomer({ ...newCustomer, alternatePhone: e.target.value });
                      handlePhoneValidation(e.target.value, true);
                    }}
                    error={alternatePhoneValidation ? !alternatePhoneValidation.isValid : false}
                    helperText={alternatePhoneValidation ? alternatePhoneValidation.message : (settings.phoneValidationEnabled ? `يجب أن يكون ${settings.phoneDigitsCount} رقم` : '')}
                    color={alternatePhoneValidation && alternatePhoneValidation.isValid ? 'success' : 'primary'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleAddNewCustomer}
                  sx={{ px: 4 }}
                >
                  ✅ حفظ العميل الجديد
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setIsAddingNewCustomer(false);
                    setNewCustomer({ name: '', phone: '', email: '', address: '', branch: '', notes: '', alternatePhone: '' });
                  }}
                  sx={{ px: 4 }}
                >
                  ❌ إلغاء
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {selectedCustomer && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{
              p: 3,
              bgcolor: 'primary.light',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              mb: 3
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 'bold' }}>
                ✅ تم اختيار العميل
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="اسم العميل"
                    value={selectedCustomer.name}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    value={selectedCustomer.phone}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف البديل"
                    value={selectedCustomer.alternatePhone || 'غير محدد'}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Grid>
              </Grid>

            {selectedCustomer.devices && selectedCustomer.devices.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">أجهزة العميل السابقة:</Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {selectedCustomer.devices.map((device) => (
                    <Grid item key={device.id}>
                      <Chip
                        label={`${device.type} - ${device.brand} ${device.model}`}
                        onClick={() => setSelectedDeviceId(device.id)}
                        color={selectedDeviceId === device.id ? "primary" : "default"}
                        variant={selectedDeviceId === device.id ? "filled" : "outlined"}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setSelectedDeviceId('');
                  }}
                  sx={{ px: 4 }}
                >
                  🔄 تغيير العميل
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* معلومات الجهاز */}
        {selectedCustomer && (
          <>
            {/* عنوان الخطوة الثانية */}
            <Box sx={{ mt: 4, mb: 3 }}>
              <Typography
                variant="h4"
                sx={{
                  mb: 1,
                  fontWeight: 'bold',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                >
                  2
                </Box>
                الخطوة الثانية
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  mb: 2,
                  ml: 6
                }}
              >
                إدخال معلومات الجهاز وتفاصيل المشكلة
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {selectedDeviceId ? (
              <Box sx={{ mb: 3 }}>
                <Typography>
                  تم اختيار جهاز من أجهزة العميل السابقة. لإدخال جهاز جديد،
                  <Button
                    sx={{ mx: 1 }}
                    size="small"
                    variant="text"
                    onClick={() => setSelectedDeviceId('')}
                  >
                    انقر هنا
                  </Button>
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>نوع الجهاز</InputLabel>
                    <Select
                      value={newDeviceData.typeId || ''}
                      onChange={(e) => handleDeviceTypeChange(e.target.value)}
                      label="نوع الجهاز"
                      disabled={loadingSettings.types}
                    >
                      {loadingSettings.types ? (
                        <MenuItem value="">
                          <CircularProgress size={20} /> جاري التحميل...
                        </MenuItem>
                      ) : (
                        deviceTypesList.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>الماركة</InputLabel>
                    <Select
                      value={newDeviceData.brandId || ''}
                      onChange={(e) => handleDeviceBrandChange(e.target.value)}
                      label="الماركة"
                      disabled={loadingSettings.brands}
                    >
                      {loadingSettings.brands ? (
                        <MenuItem value="">
                          <CircularProgress size={20} /> جاري التحميل...
                        </MenuItem>
                      ) : (
                        deviceBrandsList.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="الموديل"
                    value={newDeviceData.model || ''}
                    onChange={(e) => setNewDeviceData({ ...newDeviceData, model: e.target.value })}
                    helperText="اختياري"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="الرقم التسلسلي"
                    value={newDeviceData.serialNumber || ''}
                    onChange={(e) => setNewDeviceData({ ...newDeviceData, serialNumber: e.target.value })}
                    helperText="اختياري"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>الحجم</InputLabel>
                    <Select
                      value={newDeviceData.sizeId || ''}
                      onChange={(e) => handleDeviceSizeChange(e.target.value)}
                      label="الحجم"
                      disabled={loadingSettings.sizes}
                    >
                      {loadingSettings.sizes ? (
                        <MenuItem value="">
                          <CircularProgress size={20} /> جاري التحميل...
                        </MenuItem>
                      ) : (
                        deviceSizesList.map((size) => (
                          <MenuItem key={size.id} value={size.id}>
                            {size.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* ملحقات الجهاز */}
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              ملحقات الجهاز
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {loadingSettings.accessories ? (
                    <CircularProgress size={20} />
                  ) : (
                    deviceAccessoriesList.map((accessory) => (
                      <FormControlLabel
                        key={accessory.id}
                        control={
                          <Checkbox
                            checked={accessories.includes(accessory.name)}
                            onChange={(e) => handleAccessoryChange(accessory.name, e.target.checked)}
                          />
                        }
                        label={accessory.name}
                      />
                    ))
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="إضافة ملحق آخر"
                  value={customAccessory}
                  onChange={(e) => setCustomAccessory(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddCustomAccessory}
                          disabled={!customAccessory}
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* إدارة الأجهزة المتعددة */}
            <Box sx={{ mb: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>إدارة الأجهزة</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant={!isMultiDeviceMode ? "contained" : "outlined"}
                  onClick={() => setIsMultiDeviceMode(false)}
                >
                  جهاز واحد
                </Button>
                <Button
                  variant={isMultiDeviceMode ? "contained" : "outlined"}
                  onClick={() => setIsMultiDeviceMode(true)}
                >
                  عدة أجهزة
                </Button>
              </Box>

              {isMultiDeviceMode && (
                <Box>
                  {/* خيار نوع الطلبات */}
                  <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      نوع الطلبات:
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={separateOrders ? 'separate' : 'combined'}
                        onChange={(e) => setSeparateOrders(e.target.value === 'separate')}
                      >
                        <FormControlLabel
                          value="separate"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                طلبات منفصلة (مُوصى به)
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                كل جهاز له رقم طلب منفصل - أسهل في التتبع والإدارة
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="combined"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                طلب واحد متعدد الأجهزة
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                جميع الأجهزة في طلب واحد برقم واحد
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    الأجهزة المضافة ({devices.length})
                  </Typography>

                  {devices.length > 0 && (
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>النوع</TableCell>
                            <TableCell>الماركة</TableCell>
                            <TableCell>الموديل</TableCell>
                            <TableCell>المشكلة</TableCell>
                            <TableCell>الملحقات</TableCell>
                            <TableCell>الإجراءات</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {devices.map((device, index) => (
                            <TableRow key={index}>
                              <TableCell>{device.deviceType}</TableCell>
                              <TableCell>{device.brand}</TableCell>
                              <TableCell>{device.model || '-'}</TableCell>
                              <TableCell>{device.issue.substring(0, 30)}...</TableCell>
                              <TableCell>{device.accessories?.length || 0}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveDevice(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddDevice}
                    disabled={!newDeviceData.type || !getMaintenanceStatusText()}
                    sx={{ mb: 2 }}
                  >
                    إضافة جهاز للقائمة
                  </Button>
                </Box>
              )}
            </Box>

            {/* تفاصيل الصيانة */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
              {isMultiDeviceMode ? 'تفاصيل الجهاز الحالي' : 'حالة الصيانة'}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>الفئة الرئيسية</InputLabel>
                  <Select
                    value={selectedMaintenanceStatusCategory}
                    onChange={(e) => handleMaintenanceStatusCategoryChange(e.target.value)}
                    label="الفئة الرئيسية"
                    disabled={loadingMaintenanceStatus}
                  >
                    <MenuItem value="">
                      <em>اختر الفئة الرئيسية</em>
                    </MenuItem>
                    {maintenanceStatusCategories
                      .filter(category => category.isActive)
                      .map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>الفئة الفرعية</InputLabel>
                  <Select
                    value={selectedMaintenanceStatusSubCategory}
                    onChange={(e) => handleMaintenanceStatusSubCategoryChange(e.target.value)}
                    label="الفئة الفرعية"
                    disabled={!selectedMaintenanceStatusCategory || loadingMaintenanceStatus}
                  >
                    <MenuItem value="">
                      <em>اختر الفئة الفرعية</em>
                    </MenuItem>
                    {maintenanceStatusSubCategories
                      .filter(sub => sub.categoryId === selectedMaintenanceStatusCategory && sub.isActive)
                      .map((subCategory) => (
                        <MenuItem key={subCategory.id} value={subCategory.id}>
                          {subCategory.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useCustomStatus}
                      onChange={(e) => setUseCustomStatus(e.target.checked)}
                    />
                  }
                  label="كتابة حالة مخصصة"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="وصف المشكلة"
                  value={customMaintenanceStatus}
                  onChange={(e) => handleCustomMaintenanceStatusChange(e.target.value)}
                  helperText="يمكنك اختيار فئة من القوائم أعلاه أو كتابة وصف مخصص للمشكلة"
                  placeholder="وصف المشكلة أو اختيار من القوائم أعلاه..."

                  disabled={!useCustomStatus && selectedMaintenanceStatusSubCategory !== ''}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="تاريخ التسليم المتوقع"
                  value={maintenanceData.estimatedDeliveryDate}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, estimatedDeliveryDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="اسم الموظف المستلم"
                  value={maintenanceData.receivingEmployee || (currentUser?.name || '')}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, receivingEmployee: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>حالة الدفع</InputLabel>
                  <Select
                    value={maintenanceData.paymentStatus}
                    onChange={(e) => setMaintenanceData({ ...maintenanceData, paymentStatus: e.target.value as PaymentStatus })}
                    label="حالة الدفع"
                  >
                    <MenuItem value="unpaid">غير مدفوع</MenuItem>
                    <MenuItem value="partial">مدفوع جزئيًا</MenuItem>
                    <MenuItem value="paid">مدفوع بالكامل</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="ملاحظات إضافية"
                  value={maintenanceData.notes}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, notes: e.target.value })}
                />
              </Grid>
            </Grid>

            {/* قطع الغيار */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>قطع الغيار المستخدمة</Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>اختر منتج</InputLabel>
                  <Select
                    value={selectedItem}
                    onChange={(e) => {
                      setSelectedItem(e.target.value);
                      // تعيين السعر الافتراضي من المنتج المختار
                      const item = inventoryItems.find(item => item.id === e.target.value);
                      if (item) {
                        setItemPrice(item.sellingPrice || 0);
                      }
                    }}
                    label="اختر منتج"
                  >
                    <MenuItem value="">
                      <em>اختر منتج</em>
                    </MenuItem>
                    {inventoryItems.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name} - {formatCurrency(item.sellingPrice)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="الكمية"
                  type="number"
                  value={itemQuantity}
                  onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                  fullWidth
                  size="small"
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="سعر الوحدة"
                  type="number"
                  value={itemPrice}
                  onChange={(e) => setItemPrice(parseFloat(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">EGP</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddPart}
                  disabled={!selectedItem}
                  fullWidth
                >
                  إضافة قطعة
                </Button>
              </Grid>
            </Grid>

            {usedParts.length > 0 && (
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>المنتج</TableCell>
                      <TableCell align="center">الكمية</TableCell>
                      <TableCell align="center">السعر</TableCell>
                      <TableCell align="center">الإجمالي</TableCell>
                      <TableCell align="center">إجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usedParts.map((part, index) => (
                      <TableRow key={index}>
                        <TableCell>{part.name}</TableCell>
                        <TableCell align="center">{part.quantity}</TableCell>
                        <TableCell align="center">{part.price.toFixed(2)} ج.م</TableCell>
                        <TableCell align="center">{part.total.toFixed(2)} ج.م</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemovePart(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* القسم المالي */}
            <Box my={2}>
              <Typography variant="h6" gutterBottom>
                معلومات التكلفة
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                جميع الأسعار بالجنيه المصري (EGP) شاملة الضريبة - يمكن تعديل السعر النهائي من صفحة التفاصيل
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="التكلفة المبدئية"
                    type="number"
                    value={maintenanceData.initialCost}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setMaintenanceData({ ...maintenanceData, initialCost: value });
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">EGP</InputAdornment>,
                    }}
                    helperText="أجرة الصيانة فقط (هذه القيمة هي التكلفة النهائية)"
                    disabled={isEditingReceipt && maintenanceData.finalCost > 0}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="المبلغ المدفوع"
                    type="number"
                    value={maintenanceData.paid}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setMaintenanceData({ ...maintenanceData, paid: value });
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">EGP</InputAdornment>,
                    }}
                    helperText="المبلغ المستلم كمقدم"
                    disabled={maintenanceData.paymentStatus === 'unpaid' || maintenanceData.paymentStatus === 'paid'}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="التكلفة النهائية"
                    value={maintenanceData.finalCost.toFixed(2)}
                    onChange={(e) => {
                      if (isEditingReceipt) {
                        const value = parseFloat(e.target.value) || 0;
                        setMaintenanceData({ ...maintenanceData, finalCost: value });
                      }
                    }}
                    InputProps={{
                      readOnly: !isEditingReceipt,
                      endAdornment: <InputAdornment position="end">EGP</InputAdornment>,
                    }}
                    helperText="إجمالي التكلفة (أجرة الصيانة فقط)"
                  />
                </Grid>
                {maintenanceData.paymentStatus === 'partial' && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="المبلغ المتبقي"
                      value={maintenanceData.remainingAmount.toFixed(2)}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <InputAdornment position="end">EGP</InputAdornment>,
                      }}
                      helperText="المبلغ المتبقي للدفع"
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* خيارات إضافية */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>خيارات إضافية</Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={maintenanceData.requireCallBeforeMaintenance}
                      onChange={(e) => {
                        setMaintenanceData({
                          ...maintenanceData,
                          requireCallBeforeMaintenance: e.target.checked
                        });
                      }}
                    />
                  }
                  label="العميل يطلب الاتصال قبل الصيانة"
                />
              </Grid>
              {maintenanceData.requireCallBeforeMaintenance && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="التكلفة بعد الاتصال"
                    type="number"
                    value={maintenanceData.costAfterContact}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setMaintenanceData({ ...maintenanceData, costAfterContact: value });
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">EGP</InputAdornment>,
                    }}
                    helperText="التكلفة المتفق عليها بعد الاتصال بالعميل"
                  />
                </Grid>
              )}
            </Grid>



            {/* زر الحفظ */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{ px: 4, py: 1 }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    جاري الحفظ...
                  </>
                ) : (
                  isMultiDeviceMode && devices.length > 0
                    ? (separateOrders
                        ? `إنشاء ${devices.length} طلبات منفصلة`
                        : `إنشاء طلب واحد لـ ${devices.length} أجهزة`)
                    : 'حفظ طلب الصيانة'
                )}
              </Button>
            </Box>

            {/* إرسال رسائل الواتساب */}
            {selectedCustomer && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <WhatsAppMessageSender
                  customerName={selectedCustomer.name}
                  customerPhone={selectedCustomer.phone || selectedCustomer.alternatePhone || ''}
                  deviceType={isMultiDeviceMode && devices.length > 0
                    ? devices.map(d => d.deviceType).join(', ')
                    : (newDeviceData.type || 'غير محدد')
                  }
                  cost={maintenanceData.costAfterContact > 0 ? `${maintenanceData.costAfterContact} ${settings.currency}` : (maintenanceData.finalCost > 0 ? `${maintenanceData.finalCost} ${settings.currency}` : undefined)}
                  requireCallBeforeMaintenance={maintenanceData.requireCallBeforeMaintenance}
                  onMessageSent={(success, messageType) => {
                    if (success) {
                      let messageText = '';
                      switch (messageType) {
                        case 'deviceReceived':
                          messageText = 'رسالة استلام الجهاز';
                          break;
                        case 'deviceInProgress':
                          messageText = 'رسالة التكلفة أثناء العمل';
                          break;
                        case 'deviceCompleted':
                          messageText = 'رسالة اكتمال الإصلاح';
                          break;
                        default:
                          messageText = 'الرسالة';
                      }
                      setSnackbar({
                        open: true,
                        message: `تم إرسال ${messageText} بنجاح`,
                        severity: 'success'
                      });
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* رسالة التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>


    </Box>
  );
};

export default MaintenanceCreatePage;